import { useState, useEffect, useRef } from 'react'

interface AppRelease {
  version: string
  filename: string
  size: number
  size_human: string
  updated: string
  download_url: string
  error?: string
}

export default function Downloads() {
  const [release, setRelease] = useState<AppRelease | null>(null)
  const [releases, setReleases] = useState<AppRelease[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Upload state
  const [showUpload, setShowUpload] = useState(false)
  const [password, setPassword] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchReleases = () => {
    setLoading(true)
    Promise.all([
      fetch('/api/downloads/latest').then(res => res.json()),
      fetch('/api/downloads/list').then(res => res.json())
    ])
      .then(([latestData, listData]) => {
        if (latestData.error) {
          setError(latestData.error)
        } else {
          setRelease(latestData)
        }
        setReleases(Array.isArray(listData) ? listData : [])
      })
      .catch(() => setError('Failed to check for updates'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchReleases()
  }, [])

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    setUploadError('')
    setUploadSuccess('')

    const file = fileInputRef.current?.files?.[0]
    if (!file) {
      setUploadError('Please select a file')
      return
    }

    if (!password) {
      setUploadError('Password required')
      return
    }

    setUploading(true)

    const formData = new FormData()
    formData.append('apk', file)
    formData.append('password', password)

    try {
      const res = await fetch('/api/downloads/upload', {
        method: 'POST',
        body: formData,
      })
      const data = await res.json()

      if (!res.ok || data.error) {
        setUploadError(data.error || 'Upload failed')
      } else {
        setUploadSuccess(`Uploaded ${data.filename} (${data.size_human})`)
        if (fileInputRef.current) fileInputRef.current.value = ''
        fetchReleases()
      }
    } catch {
      setUploadError('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (filename: string) => {
    if (!password) {
      setUploadError('Enter password first')
      return
    }

    if (!confirm(`Delete ${filename}?`)) return

    try {
      const res = await fetch(`/api/downloads/delete?filename=${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { 'X-Upload-Password': password },
      })
      const data = await res.json()

      if (!res.ok || data.error) {
        setUploadError(data.error || 'Delete failed')
      } else {
        setUploadSuccess(`Deleted ${filename}`)
        fetchReleases()
      }
    } catch {
      setUploadError('Delete failed')
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl text-center space-y-8">
        {/* App Icon / Title */}
        <div className="space-y-4">
          <h1 className="text-5xl font-bold tracking-tight">OBACast</h1>
          <p className="text-xl text-gray-400">
            Android TV Streaming App
          </p>
        </div>

        {/* Version Info & Download */}
        <div className="bg-gray-900 rounded-2xl p-8 space-y-6 border border-gray-800">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : error && !release ? (
            <div className="py-8">
              <p className="text-gray-400 text-lg">{error}</p>
              <p className="text-gray-500 mt-2">No APK files have been uploaded yet.</p>
            </div>
          ) : release ? (
            <>
              <div className="space-y-2">
                <div className="text-3xl font-semibold">v{release.version}</div>
                <div className="text-gray-400">
                  {release.size_human} &middot; Updated {release.updated}
                </div>
              </div>

              <a
                href={release.download_url}
                className="inline-block w-full max-w-sm bg-blue-600 hover:bg-blue-700 text-white text-xl font-semibold py-4 px-8 rounded-xl transition-colors"
              >
                Download APK
              </a>
            </>
          ) : null}
        </div>

        {/* Installation Instructions */}
        <div className="bg-gray-900 rounded-2xl p-8 text-left space-y-4 border border-gray-800">
          <h2 className="text-2xl font-semibold text-center">Installation on Shield</h2>
          <ol className="space-y-3 text-lg text-gray-300 list-decimal list-inside">
            <li>Open this page in the Shield TV browser</li>
            <li>Tap the <strong>Download APK</strong> button above</li>
            <li>When the download completes, open the APK file</li>
            <li>If prompted, allow installation from unknown sources</li>
            <li>Follow the on-screen prompts to install</li>
            <li>Launch OBACast from your apps list</li>
          </ol>
        </div>

        {/* Admin Upload Section */}
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
          >
            {showUpload ? 'Hide Admin' : 'Admin'}
          </button>

          {showUpload && (
            <div className="mt-6 space-y-4 text-left">
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    placeholder="Upload password"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">APK File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".apk"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
                  />
                </div>

                {uploadError && (
                  <p className="text-red-400 text-sm">{uploadError}</p>
                )}
                {uploadSuccess && (
                  <p className="text-green-400 text-sm">{uploadSuccess}</p>
                )}

                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  {uploading ? 'Uploading...' : 'Upload APK'}
                </button>
              </form>

              {/* Release list with delete */}
              {releases.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <h3 className="text-lg font-semibold mb-3">All Releases</h3>
                  <div className="space-y-2">
                    {releases.map(r => (
                      <div key={r.filename} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-2">
                        <div>
                          <span className="font-medium">v{r.version}</span>
                          <span className="text-gray-400 text-sm ml-2">{r.size_human}</span>
                        </div>
                        <button
                          onClick={() => handleDelete(r.filename)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-gray-600 text-sm">
          OBACast &middot; Built for NVIDIA Shield TV
        </p>
      </div>
    </div>
  )
}
